import { createReducer, on } from '@ngrx/store';
import {
  ACCouponsFilterChanged,
  ACPanelItemsChanged,
  ACPanelOrderChanged,
  ACPanelPageChanged,
  ACPanelSortChanged,
  ACTagsStatusChanged,
  GetCustomerCampaignsCountSuccess,
  GetCustomerCampaignsTableFailed,
  GetCustomerCampaignsTableLoading,
  GetCustomerCampaignsTableSuccess,
  GetCustomerCouponsCountSuccess,
  GetCustomerCouponsTableFailed,
  GetCustomerCouponsTableLoading,
  GetCustomerCouponsTableSuccess,
  GetCustomerTagsCountACSuccess,
  GetCustomerTagsTableACFailed,
  GetCustomerTagsTableACLoading,
  GetCustomerTagsTableACSuccess,
  GetCustomerTicketsCountSuccess,
  GetCustomerTicketsTableFailed,
  GetCustomerTicketsTableLoading,
  GetCustomerTicketsTableSuccess,
  GetCustomerTransactionsCountSuccess,
  GetCustomerTransactionsTableFailed,
  GetCustomerTransactionsTableLoading,
  GetCustomerTransactionsTableSuccess,
  ViewCustomerSearchPage,
} from '../actions/all-customers-summary.actions';

export interface PanelPagination {
  pageIndex: number;
  pageSize: number;
  sortField: string;
  sortOrder: string;
}

export interface CustomerSummaryState {
  campaigns: any[];
  campaignsCount: number;
  campaignsPagination: PanelPagination;
  campaignsLoading: boolean;
  tickets: any[];
  ticketsCount: number;
  ticketsPagination: PanelPagination;
  ticketsLoading: boolean;
  transactions: any[];
  transactionsCount: number;
  transactionsPagination: PanelPagination;
  transactionsLoading: boolean;
  customerTags: any[];
  customerTagsCount: number;
  customerTagsPagination: PanelPagination;
  customerTagsActive: boolean;
  customerTagsLoading: boolean;
  coupons: any[];
  couponsCount: number;
  couponsPagination: PanelPagination;
  couponsUsed: boolean | null;
  couponsGiven: boolean | null;
  couponsCouponSetId: number | null;
  couponsLoading: boolean;
}

const initialState: CustomerSummaryState = {
  campaigns: [],
  campaignsCount: 0,
  campaignsPagination: {
    pageIndex: 0,
    pageSize: 10,
    sortField: 'CREATION_DATE',
    sortOrder: 'DESC',
  },
  campaignsLoading: false,
  tickets: [],
  ticketsCount: 0,
  ticketsPagination: { pageIndex: 0, pageSize: 10, sortField: 'id', sortOrder: 'DESC' },
  ticketsLoading: false,
  transactions: [],
  transactionsCount: 0,
  transactionsPagination: {
    pageIndex: 0,
    pageSize: 10,
    sortField: 'INVOICE_DATE',
    sortOrder: 'DESC',
  },
  transactionsLoading: false,
  customerTags: [],
  customerTagsCount: 0,
  customerTagsPagination: {
    pageIndex: 0,
    pageSize: 10,
    sortField: 'START_DATE',
    sortOrder: 'DESC',
  },
  customerTagsActive: true,
  customerTagsLoading: false,
  coupons: [],
  couponsCount: 0,
  couponsPagination: { pageIndex: 0, pageSize: 10, sortField: 'DATE_CREATED', sortOrder: 'DESC' },
  couponsUsed: null,
  couponsGiven: null,
  couponsCouponSetId: null,
  couponsLoading: false,
};

export const CustomerSummaryReducer = createReducer(
  initialState,
  on(GetCustomerCampaignsTableLoading, (state) => ({ ...state, campaignsLoading: true })),
  on(GetCustomerCampaignsTableSuccess, (state, { res }) => ({
    ...state,
    campaigns: res || [],
    campaignsLoading: false,
  })),
  on(GetCustomerCampaignsTableFailed, (state) => ({ ...state, campaignsLoading: false })),
  on(GetCustomerCampaignsCountSuccess, (state, { res }) => ({ ...state, campaignsCount: res })),
  on(GetCustomerTicketsTableLoading, (state) => ({ ...state, ticketsLoading: true })),
  on(GetCustomerTicketsTableSuccess, (state, { res }) => ({
    ...state,
    tickets: res || [],
    ticketsLoading: false,
  })),
  on(GetCustomerTicketsTableFailed, (state) => ({ ...state, ticketsLoading: false })),
  on(GetCustomerTicketsCountSuccess, (state, { res }) => ({ ...state, ticketsCount: res })),
  on(GetCustomerTransactionsTableLoading, (state) => ({ ...state, transactionsLoading: true })),
  on(GetCustomerTransactionsTableSuccess, (state, { res }) => ({
    ...state,
    transactions: res || [],
    transactionsLoading: false,
  })),
  on(GetCustomerTransactionsTableFailed, (state) => ({ ...state, transactionsLoading: false })),
  on(GetCustomerTransactionsCountSuccess, (state, { res }) => ({
    ...state,
    transactionsCount: res,
  })),
  on(GetCustomerTagsTableACLoading, (state) => ({ ...state, customerTagsLoading: true })),
  on(GetCustomerTagsTableACSuccess, (state, { res }) => ({
    ...state,
    customerTags: res || [],
    customerTagsLoading: false,
  })),
  on(GetCustomerTagsTableACFailed, (state) => ({ ...state, customerTagsLoading: false })),
  on(GetCustomerTagsCountACSuccess, (state, { res }) => ({ ...state, customerTagsCount: res })),
  on(GetCustomerCouponsTableLoading, (state) => ({ ...state, couponsLoading: true })),
  on(GetCustomerCouponsTableSuccess, (state, { res }) => ({
    ...state,
    coupons: res || [],
    couponsLoading: false,
  })),
  on(GetCustomerCouponsTableFailed, (state) => ({ ...state, couponsLoading: false })),
  on(GetCustomerCouponsCountSuccess, (state, { res }) => ({ ...state, couponsCount: res })),
  on(ACPanelItemsChanged, (state, { panel, pageSize }) => ({
    ...state,
    [`${panel}Pagination`]: {
      ...(state[`${panel}Pagination` as keyof CustomerSummaryState] as PanelPagination),
      pageSize,
      pageIndex: 0,
    },
  })),
  on(ACPanelSortChanged, (state, { panel, sortField }) => ({
    ...state,
    [`${panel}Pagination`]: {
      ...(state[`${panel}Pagination` as keyof CustomerSummaryState] as PanelPagination),
      sortField,
      pageIndex: 0,
    },
  })),
  on(ACPanelOrderChanged, (state, { panel, sortOrder }) => ({
    ...state,
    [`${panel}Pagination`]: {
      ...(state[`${panel}Pagination` as keyof CustomerSummaryState] as PanelPagination),
      sortOrder,
      pageIndex: 0,
    },
  })),
  on(ACPanelPageChanged, (state, { panel, pageIndex }) => ({
    ...state,
    [`${panel}Pagination`]: {
      ...(state[`${panel}Pagination` as keyof CustomerSummaryState] as PanelPagination),
      pageIndex,
    },
  })),
  on(ACTagsStatusChanged, (state, { active }) => ({
    ...state,
    customerTagsActive: active,
    customerTagsPagination: { ...state.customerTagsPagination, pageIndex: 0 },
  })),
  on(ACCouponsFilterChanged, (state, { used, given, couponSetId }) => ({
    ...state,
    couponsUsed: used,
    couponsGiven: given,
    couponsCouponSetId: couponSetId,
    couponsPagination: { ...state.couponsPagination, pageIndex: 0 },
  })),
  on(ViewCustomerSearchPage, () => initialState),
);
